import { Directive, ElementRef, inject, Input, OnInit, Renderer2 } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { filter } from 'rxjs/operators';
import { UserAuthFacade } from '@state/user-auth';
import { PermissionEnum, ROLES_KEYS, SupportingCompanyAccessLevel, User } from '@interfaces';

@Directive({
  selector: '[appUserDetailsLink]',
  standalone: false,
})
export class UserDetailsLinkDirective implements OnInit {
  private readonly userAuthFacade = inject(UserAuthFacade);
  private readonly el = inject(ElementRef);
  private readonly renderer = inject(Renderer2);

  @Input() requiredPermissions: PermissionEnum[] = [];
  @Input() requiredSupportingCompany: { accessLevel: SupportingCompanyAccessLevel; companyId: string } | null = null;

  ngOnInit(): void {
    this.updateLinkState();
  }

  private async updateLinkState(): Promise<void> {
    const myself = await firstValueFrom(this.userAuthFacade.myself$.pipe(filter(myself => !!myself?.id)));
    const hasAccess = this.hasAllPermissions(myself, this.requiredPermissions);
    let hasCompanyAccessLevel = true;
    const { role, supportingCompanies } = myself;

    // If this is not Admin -> it's Employee | Team Leader | Field Consultant
    if (role.key !== ROLES_KEYS.Admin && this.requiredSupportingCompany !== null) {
      // These roles MUST have supportingCompanies populated!
      // If 'supportingCompanies' is empty -> user account is invalid.
      if (!supportingCompanies?.length) {
        hasCompanyAccessLevel = false;
      } else {
        const { accessLevel, companyId } = this.requiredSupportingCompany;
        if (accessLevel && companyId) {
          hasCompanyAccessLevel = !!myself.supportingCompanies.find(
            sc => sc[accessLevel] && sc.companyId === companyId
          );
        }
      }
    }

    if (!hasAccess || !hasCompanyAccessLevel) {
      const element = this.el.nativeElement;
      this.renderer.addClass(element, 'non-clickable');
    }
  }

  private hasAllPermissions(user: User, requiredPermissions: PermissionEnum[]) {
    return requiredPermissions.every(permission =>
      user.role.permissions.some(userPermission => userPermission.key === permission)
    );
  }
}
